import { Stack } from "@mui/material";
import {
  Grid,
  GridColumn,
  GridCustomCellProps,
  GridDetailRowProps,
  GridRowClickEvent,
} from "@progress/kendo-react-grid";
import "@progress/kendo-theme-default/dist/all.css";
import clsx from "clsx";
import NotificationZone from "components/NotificationZone/NotificationZone";
import { DetailsWindow } from "components/WarehouseElements/Grid/DetailsWindow/DetailsWindow";
import { BASE_URL_WAREHOUSE } from "constants/api/routeGlobals";
import {
  NOTIFICATION_EVENT,
  STATUS_CHANGED_EVENT
} from "constants/events";
import { EMPTY_DIAMOND_CLASSNAME } from "constants/globals";
import { StatusChangedReceivedEvent } from "events/Event";
import { useAppDispatch } from "hooks/StoreHooks";
import { useElementEtat } from "hooks/useElementDetails";
import { OutletContext } from "pages/Home/Home";
import { JSX, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router";
import { StatusData, StatusResource } from "store/Slices/Status/statusSlice";
import { setOpenedDetailId } from "store/Slices/gridEventSlice";
import { useAppSelector } from "store/store";
import "./EtatParc.scss";
import { ElapsedRenderer, ModeRenderer, OccupeRenderer, OperationEnCoursRenderer, ReservationRenderer, VerifierLeRenderer } from "./EtatParcRenderer";

const SUBBED_EVENTS_LIST = [STATUS_CHANGED_EVENT, NOTIFICATION_EVENT];
export function useMinuteTicker(): Date {
  const [now, setNow] = useState<Date>(new Date());

  useEffect(() => {
    let interval: NodeJS.Timeout;
    let timeout: NodeJS.Timeout;

    const schedule = () => {
      const current = new Date();

      // ms until next minute (00 seconds)
      const msUntilNextMinute =
        (60 - current.getSeconds()) * 1000 - current.getMilliseconds();

      timeout = setTimeout(() => {
        setNow(new Date());

        // After first alignment, update every 60 seconds
        interval = setInterval(() => {
          setNow(new Date());
        }, 60_000);
      }, msUntilNextMinute);
    };

    schedule();

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, []);

  return now;
}


type DetailInfo = {
  id: string;
  posTop: number;
  posLeft: number;
};


export function GridEtatParc(currentLocationStoreNb: number): JSX.Element {
  const dispatch = useAppDispatch();
  const everyMinuteTimer = useMinuteTicker();
  const [parcs, setParcs] = useState<any[]>([]);
  const latestParcs = useRef(parcs);
  const [currentMinutes, setCurrentMinutes] = useState<Date>(new Date());
  const [currentDetailId, setCurrentDetailId] = useState<DetailInfo>({
    id: "",
    posTop: 0,
    posLeft: 0,
  });
  const isCurrentDetailsWindowOpen = useAppSelector(
    (state) => state.gridEventSlice.openedDetailId === currentDetailId.id,
  );


const DetailComponent = (props: GridDetailRowProps) => {
  const [statusList, setStatusList] = useState<StatusResource>();

  const {
      detailsInfos,
      isLoading,
      error: queryError,
    } = useElementEtat( currentLocationStoreNb, props.dataItem.parcCd );

  if (detailsInfos && statusList == undefined) 
    setStatusList(detailsInfos);
  if (isLoading || statusList == undefined) return <></>; 

  function createEtiquetteClassName(
    status: StatusData
    
  ): string {
    return clsx(
      status.isOccupied && "OCC",
      status.hasInventory && "INV",
      (status.hasPallet && !status.hasCage) && "PAL",
      status.hasCage && "CAG"
    );
  }

  const render = function(etat: StatusData) {
    const clsx = createEtiquetteClassName(etat);
    return (
    <div key={"db-" + etat.id} id={"db-" + etat.id} className="emplacement">
      <div className={ "emplacement-background " + clsx}>
        <div className={EMPTY_DIAMOND_CLASSNAME}></div>
      </div>
      <div className={ "emplacement-text " + clsx}>
          {etat.id.replace(props.dataItem.parcCd, "")}
      </div>
    </div>      
    );
  }

  const binsList = statusList.statusPlan.map((etat: StatusData) => (
    render(etat)
  ));
  return (
        <section>
      <Stack direction="row" spacing={1}>
        { binsList}
      </Stack>
      </section>
  );

};


  useEffect(() => {
    latestParcs.current = parcs;
  }, [parcs]);

  useEffect(() => {
    StatusChangedReceivedEvent.subscribe((e) => { 
      const newParcs = latestParcs.current.map((parc) => {
        if (parc.parcCd === e.id) {
          parc.modeOperation = e.operationType;
          parc.reservation = e.reservationType;
          parc.tauxOccupation = e.tauxOccupation;
          parc.operEnAttente = e.isOperationInProgress;
          parc.verifierLe = e.verifierLe;
          parc.verifierParUsager = e.verifierParUsager;
          parc.uniteDTPlusVieux = e.uniteDTPlusVieux;
          parc.uniteDTPlusJeune = e.uniteDTPlusJeune;
          return parc;
        } else {
          return parc;
        }
      });
      setParcs(newParcs);      

    });
  }, []);  

  const handleCellClick = (event: GridRowClickEvent) => {
    const posX = event.syntheticEvent.pageX;
    const posY = event.syntheticEvent.pageY;
    if (
      isCurrentDetailsWindowOpen &&
      currentDetailId.id === event.dataItem.parcCd
    ) {
      setCurrentDetailId({ id: "", posTop: 0, posLeft: 0 });
      dispatch(setOpenedDetailId(null));
      return;
    }
    setCurrentDetailId({
      id: event.dataItem.parcCd,
      posTop: posY,
      posLeft: posX,
    });
    dispatch(setOpenedDetailId(event.dataItem.parcCd));
  };

  const fetchData = (storeNb: number) => {
    fetch(BASE_URL_WAREHOUSE + `sge/${storeNb}/suivi`)
      .then((response) => response.json())
      .then((data) => {
        setParcs(data);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement", error);
      });
  };

  const VerifierLeRendererTimerTriggered = (extraProp) => {
    return function CellWrap(props: GridCustomCellProps) {
      return <VerifierLeRenderer {...props} />;
    }

  }

  const ElapsedRendererTimerTriggered = (extraProp) => {
    return function CellWrap(props: GridCustomCellProps) {
      return <ElapsedRenderer {...props} />;
    }

  }

  useEffect(() => {
    fetchData(currentLocationStoreNb);
  }, [currentLocationStoreNb]);

  useEffect(() => {
    setCurrentMinutes(everyMinuteTimer);
  }, [everyMinuteTimer]);

  const resetPopup = () => {
    setCurrentDetailId({ id: "", posTop: 0, posLeft: 0 });
  };

  return (
    <>
      <div
        id="page-etat-parc"
        key={currentLocationStoreNb}
        style={{ padding: "20px", height: "100%", overflow: "hidden" }}
      >
          <div>
            {isCurrentDetailsWindowOpen && (
              <DetailsWindow
                key={currentDetailId.id}
                id={currentDetailId.id}
                posTop={currentDetailId.posTop}
                posLeft={currentDetailId.posLeft}
                useStoreNb={currentLocationStoreNb}
              />
            )}
          </div>
          <Stack direction="row" spacing={0} height={ "100%" }>
          <Grid
            data={parcs}
            style={{ height: "100%" }}
            dataItemKey="parcCd"
            sortable={true}
            //detail={DetailComponent}
            pageable={false}
            autoProcessData={true}
            filterable={true}
            onRowClick={handleCellClick}
            onScroll={resetPopup}
            onDataStateChange={resetPopup}
          >
            <GridColumn field="description" title="Parc" width={"150px"} />
            <GridColumn
              field="modeOperation"
              title="Mode"
              cells={{ data: ModeRenderer }}
              width={"150px"}
            />
            <GridColumn
              field="reservation"
              title="Réservation"
              cells={{ data: ReservationRenderer }}
              width={"150px"}
            />
            <GridColumn
              field="tauxOccupation"
              title="Occupé"
              width={"230px"}
              filterable={false}
              cells={{ data: OccupeRenderer }}
            />
            <GridColumn
              field="uniteDTPlusVieux"
              title="Plus ancienne"
              width={"150px"}
              filterable={false}
              cells={{ data: ElapsedRendererTimerTriggered(currentMinutes) }}
            />
            <GridColumn
              field="uniteDTPlusJeune"
              title="Plus récente"
              width={"150px"}
              filterable={false}
              cells={{ data: ElapsedRendererTimerTriggered(currentMinutes) }}
            />
            <GridColumn
              field="verifierLe"
              title="Vérifier le"
              filterable={false}
              cells={{ data: VerifierLeRendererTimerTriggered(currentMinutes) }}
            />
            <GridColumn
              field="operEnAttente"
              title="Opération"
              filterable={false}
              cells={{ data: OperationEnCoursRenderer }}
            />
          </Grid>
          <div style={ { border: "1px solid #dcdcdc", height: "100%" }}>
          <NotificationZone/>
          </div>
          </Stack>
          </div>
    </>
  );
}

export function EtatParc(): JSX.Element {
  const { storeNb: currentLocationStoreNb }: OutletContext = useOutletContext();

  return GridEtatParc(currentLocationStoreNb);
}
